import { ClinicalQueryRoles, Role, type ClinicalQueryRole } from '@h20/contracts/types/auth';
import type { ClinicalQueryStats } from '@h20/contracts/types/api/clinQuery/ClinicalQueryModels';
import type { PulseListTooltipItem } from '@h20-shared/pulse-components/pulse-list-tooltip';

export type QueryStatusCounts = {
    readonly open: number;
    readonly answered: number;
    readonly closed: number;
};

/** Counts every query that is not closed (open and answered) */
export function getActiveQueryCount(stats: ClinicalQueryStats | null | undefined): number {
    if (!stats) {
        return 0;
    }
    return ClinicalQueryRoles.reduce(
        (sum, role) => sum + stats.open[role] + stats.answered[role],
        0
    );
}

export function getQueryStatusCounts(stats: ClinicalQueryStats): QueryStatusCounts {
    const open = ClinicalQueryRoles.reduce((sum, role) => sum + stats.open[role], 0);
    const answered = ClinicalQueryRoles.reduce((sum, role) => sum + stats.answered[role], 0);
    return { open, answered, closed: stats.total - open - answered };
}

const awaitingLabelByRole: Readonly<Record<ClinicalQueryRole, string>> = {
    [Role.siteAdmin]: 'QueryCell.AwaitingSiteCount',
    [Role.monitor]: 'QueryCell.AwaitingMonitorCount',
    [Role.dataManagement]: 'QueryCell.AwaitingDMCount',
};

/** One "Awaiting <role>: <count>" line for each role whose count is above 0. */
function awaitingLines(countFor: (role: ClinicalQueryRole) => number): PulseListTooltipItem[] {
    return ClinicalQueryRoles.flatMap((role) => {
        const count = countFor(role);
        return count > 0 ? [{ label: awaitingLabelByRole[role], params: { count } }] : [];
    });
}

export function queryStatusTooltipItems(stats: ClinicalQueryStats): PulseListTooltipItem[] {
    const counts = getQueryStatusCounts(stats);
    return [
        {
            label: 'QueryCell.OpenCount',
            params: { count: counts.open },
            details: awaitingLines((role) => stats.open[role]),
        },
        {
            label: 'QueryCell.AnsweredCount',
            params: { count: counts.answered },
            details: awaitingLines((role) => stats.answered[role]),
        },
        { label: 'QueryCell.ClosedCount', params: { count: counts.closed } },
    ];
}

export function queryRoleTooltipItems(stats: ClinicalQueryStats): PulseListTooltipItem[] {
    const lines = awaitingLines((role) => stats.open[role] + stats.answered[role]);
    if (lines.length === 0) {
        return [{ label: 'QueryCell.NoOpenQueries' }];
    }
    return lines;
}
